// Utility functions for exporting frames as images

export type ExportFormat = 'png' | 'jpeg'

/**
 * Export a canvas as an image file (triggers download)
 */
export async function exportCanvasAsImage(
  canvas: HTMLCanvasElement,
  filename: string,
  format: ExportFormat = 'png',
  quality: number = 0.92
): Promise<void> {
  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png'
  const extension = format === 'jpeg' ? '.jpg' : '.png'
  
  // Update filename extension
  const finalFilename = filename.replace(/\.(png|jpg|jpeg)$/i, extension)
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image blob'))
          return
        }

        // Create download link
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = finalFilename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        resolve()
      },
      mimeType,
      quality
    )
  })
}

/**
 * Get estimated file size (rough estimate) for a frame export
 */
export function estimateFileSize(
  width: number,
  height: number,
  format: ExportFormat,
  quality: number = 0.92
): number {
  const pixels = width * height
  
  if (format === 'png') {
    // PNG is typically 2-4 bytes per pixel with compression
    return Math.round(pixels * 2.5)
  } else {
    // JPEG varies greatly with quality and content
    const qualityFactor = 0.1 + (quality * 0.9) // 0.1 to 1.0
    return Math.round(pixels * 0.3 * qualityFactor)
  }
}
